import React, { createContext, useState, useContext, useEffect } from 'react';
import { getAPIBaseURL } from '../utils/apiConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Token state is kept for backward compatibility but httpOnly cookies are primary
  const [token, setToken] = useState(null);

  // API base URL - with devtunnels support
  const API_BASE = getAPIBaseURL();

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuth();

    // Listen for SSO token updates (backward compatibility)
    const handleSSOTokenUpdate = (event) => {
      const { user: newUser } = event.detail;
      if (newUser) {
        setUser(newUser);
      }
    };

    window.addEventListener('sso-token-updated', handleSSOTokenUpdate);
    return () => window.removeEventListener('sso-token-updated', handleSSOTokenUpdate);
  }, []);

  // Attempt to refresh the access token using refresh token cookie
  const refreshAccessToken = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Send httpOnly refresh token cookie
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.accessToken) {
          setToken(data.accessToken);
          localStorage.setItem('token', data.accessToken);
          return { success: true, accessToken: data.accessToken };
        }
      }
      return { success: false };
    } catch (error) {
      console.error('Token refresh failed:', error);
      return { success: false };
    }
  };

  const checkAuth = async () => {
    try {
      // Get stored token from localStorage
      const storedToken = localStorage.getItem('token');

      // Build headers - always include token if available
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add Authorization header if we have a stored token
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      // Use credentials: 'include' to send httpOnly cookies as backup
      const response = await fetch(`${API_BASE}/auth/me`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Keep the stored token
        if (storedToken) {
          setToken(storedToken);
        }
      } else if (response.status === 401) {
        // Access token expired - try to refresh
        const refreshResult = await refreshAccessToken();

        if (refreshResult.success) {
          // Retry auth check with new token
          const retryResponse = await fetch(`${API_BASE}/auth/me`, {
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${refreshResult.accessToken}`
            }
          });

          if (retryResponse.ok) {
            const data = await retryResponse.json();
            setUser(data.user);
          } else {
            // Refresh worked but still can't auth - clear everything
            setUser(null);
            setToken(null);
            localStorage.removeItem('token');
          }
        } else {
          // Refresh failed - user needs to re-login
          setUser(null);
          setToken(null);
          localStorage.removeItem('token');
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Network error - don't clear auth, user might just be offline
      // Only clear if we don't have a stored token
      if (!localStorage.getItem('token')) {
        setUser(null);
        setToken(null);
      }
    }
    setLoading(false);
  };

  const login = async (identifier, password) => {
    try {
      const loginUrl = `${API_BASE}/auth/login`;

      const response = await fetch(loginUrl, {
        method: 'POST',
        credentials: 'include', // Receive and store httpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ identifier, password })
      });

      // Check if response has content before parsing JSON
      const responseText = await response.text();

      let data;

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        return { success: false, message: 'Invalid server response' };
      }

      if (response.ok) {
        setUser(data.user);
        // Store the actual access token for API calls
        if (data.accessToken) {
          setToken(data.accessToken);
          localStorage.setItem('token', data.accessToken);
        }
        return { success: true, user: data.user };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        credentials: 'include', // Receive and store httpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        // Store the actual access token for API calls
        if (data.accessToken) {
          setToken(data.accessToken);
          localStorage.setItem('token', data.accessToken);
        }
        return { success: true, user: data.user };
      } else {
        return { success: false, message: data.message, errors: data.errors };
      }
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookies server-side
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const forgotPassword = async (identifier) => {
    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ identifier })
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  // Manual user update (for registration flow)
  const setUserData = (userData) => {
    setUser(userData);
    setToken('cookie-based');
  };

  // Refresh user data from server (for after verification, etc.)
  const refreshUser = async () => {
    try {
      const storedToken = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };

      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      const response = await fetch(`${API_BASE}/auth/me`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        return { success: true, user: data.user };
      } else if (response.status === 401) {
        // Try to refresh token
        const refreshResult = await refreshAccessToken();
        if (refreshResult.success) {
          // Retry with new token
          const retryResponse = await fetch(`${API_BASE}/auth/me`, {
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${refreshResult.accessToken}`
            }
          });
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            setUser(data.user);
            return { success: true, user: data.user };
          }
        }
      }
      return { success: false };
    } catch (error) {
      console.error('Refresh user failed:', error);
      return { success: false };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    checkAuth,
    setUserData,
    refreshUser,
    refreshAccessToken,
    isAuthenticated: !!user,
    isPropertyOwner: user?.userType === 'property-owner',
    isProfessional: user?.userType === 'professional',
    isAdmin: user?.userType === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
